import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Flag, ArrowLeft, MessageSquareDashed } from "lucide-react";
import { api, errMsg, fmtTime } from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/RoleBadge";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ReportDialog } from "@/components/chat/ReportDialog";
import { toast } from "sonner";

export function DmPanel() {
  const { activeConv, setActiveConv } = useChat();
  const { roles } = useAuth();
  const [convs, setConvs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const lastRef = useRef(null);
  const bottomRef = useRef(null);

  const loadConvs = useCallback(() => api.get("/dm/conversations").then((r) => setConvs(r.data)).catch(() => {}), []);

  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 6000);
    return () => clearInterval(t);
  }, [loadConvs]);

  useEffect(() => {
    if (!activeConv) return;
    lastRef.current = null;
    setMsgs([]);
    const poll = async () => {
      try {
        const params = lastRef.current ? { after: lastRef.current } : {};
        const { data } = await api.get(`/dm/conversations/${activeConv}/messages`, { params });
        if (data.length) {
          lastRef.current = data[data.length - 1].created_at;
          setMsgs((p) => { const ids = new Set(p.map((m) => m.id)); return [...p, ...data.filter((m) => !ids.has(m.id))]; });
        }
      } catch (e) { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [activeConv]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const conv = convs.find((c) => c.id === activeConv);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeConv) return;
    try {
      const { data } = await api.post(`/dm/conversations/${activeConv}/messages`, { text });
      setMsgs((p) => [...p, data]);
      lastRef.current = data.created_at;
      setText("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!activeConv) {
    return (
      <div className="flex-1 overflow-y-auto p-4" data-testid="dm-conversation-list">
        {convs.length === 0 && (
          <div className="text-center text-slate-500 py-14 px-6">
            <MessageSquareDashed className="h-10 w-10 mx-auto text-slate-700" />
            <p className="text-sm mt-3">Henüz özel sohbetin yok.</p>
            <p className="text-xs mt-1 text-slate-600">Genel sohbette bir kullanıcının adına tıklayıp "Sohbet Et" diyebilirsin. Karşı taraf onaylayınca burada görünür.</p>
          </div>
        )}
        {convs.map((c) => (
          <button key={c.id} data-testid="dm-conversation-item" onClick={() => setActiveConv(c.id)} className="w-full text-left rounded-xl border border-slate-800 bg-slate-900/60 hover:border-cyan-500/40 p-3.5 mb-2 transition-colors">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm text-slate-100">{c.other_username}</span>
              <span className="text-[10px] text-slate-600 font-mono-cx">{fmtTime(c.last_message_at)}</span>
            </div>
            {c.other_mod_role && roles[c.other_mod_role] && <RoleBadge role={roles[c.other_mod_role]} size="xs" />}
            <p className="text-xs text-slate-500 truncate mt-1">{c.last_message || "Sohbet başladı"}</p>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="dm-chat-view">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-3">
        <button data-testid="dm-back-button" onClick={() => setActiveConv(null)} className="h-8 w-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-300"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-100 truncate" data-testid="dm-other-username">{conv?.other_username || "Sohbet"}</p>
          <p className="text-[10px] text-slate-500 font-mono-cx">Özel mesaj</p>
        </div>
        {conv && (
          <button data-testid="dm-report-button" onClick={() => setReportOpen(true)} className="h-8 px-3 rounded-lg bg-red-950/60 border border-red-500/40 text-red-300 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-red-950 transition-colors">
            <Flag className="h-3.5 w-3.5" /> Bildir
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="dm-messages">
        {msgs.length === 0 && <p className="text-center text-xs text-slate-600 py-10">Sohbet başladı. İlk mesajı yaz!</p>}
        {msgs.map((m) => <ChatMessage key={m.id} msg={m} showActions={false} />)}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="p-4 border-t border-slate-800 flex gap-2">
        <input data-testid="dm-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Mesaj yaz..." maxLength={1000} className="cx-input flex-1 px-4 text-sm outline-none" />
        <button data-testid="dm-send" type="submit" disabled={!text.trim()} className="h-11 w-11 rounded-lg cx-btn-primary flex items-center justify-center disabled:opacity-50"><Send className="h-4 w-4" /></button>
      </form>
      {conv && <ReportDialog open={reportOpen} onOpenChange={setReportOpen} targetId={conv.other_user_id} username={conv.other_username} conversationId={conv.id} />}
    </div>
  );
}
